import React, { ReactElement, useCallback, useMemo, useRef, useState } from 'react'
import { ViewStyle } from 'react-native'

import { isFunction, noop } from 'lodash-es'

import { TypeAhead } from './TypeAhead'
import { TypeAheadCustomOptionsConfig, ValueFetchMode } from './TypeAheadCustomOptionsConfig'
import { TypeAheadValue } from '../../../organisms/formBody/types'
import { TooltipMaxWidth, TooltipPlacement, ToolTipTriggerConfig } from '../../tooltip/Tooltip'

/**
 * Converts a value to a native array.
 * Returns an empty array for falsy values.
 * Note: Immutable.js Lists should be converted to native arrays using .toJS()
 * at the call site before passing to TypeAheadFieldAdapter.
 */
const toNativeArray = <T,>(value: T | T[] | undefined | null): T[] => {
  if (!value) return []
  // Handle native array
  if (Array.isArray(value)) return value
  // Handle single value
  return [value as T]
}

type FilterByFieldProps<T> = {
  text: string
  selected: T[]
  labelKey: (option: T) => string
  multiple: boolean
}

type TypeAheadFieldAdapterProps<T = any> = {
  onChange?: (value: T[]) => void
  disabled?: boolean
  readOnly?: boolean
  options: T[]
  placeholder?: string
  multiSelect?: boolean
  meta?: {
    touched?: boolean
    error?: string
    submitError?: string
    active?: boolean
  }
  input: {
    onChange?: (value: any) => void
    onBlur?: (value?: any) => void
    onFocus?: () => void
    value?: T | T[]
    name: string
  }
  label?: string
  emptyLabel?: string
  labelKey?: string | ((option: T) => string)
  shouldForceTouch?: boolean
  baseInputStyle?: ViewStyle
  inputContainerStyle?: ViewStyle
  dropdownContainerStyle?: ViewStyle
  filterBy?: (option: T, fieldProps: FilterByFieldProps<T>) => boolean
  renderMenuItemChildren?: (option: T, fieldProps: FilterByFieldProps<T>) => ReactElement
  /** Keep dropdown always open (note: limited support - dropdown opens on focus) */
  alwaysOpen?: boolean
  /** Called when the user types in the search input — use this to access the current search text (e.g. for custom highlighting in renderMenuItemChildren) */
  onInputChange?: (text: string) => void
  /** Position dropdown menu absolutely to overlay other items instead of pushing them down */
  fixedDropdownMenuPosition?: boolean
  toolTipContent?: string | ReactElement
  toolTipTriggerConfig?: ToolTipTriggerConfig
  toolTipPlacement?: TooltipPlacement
  toolTipHoverEnabled?: boolean
  toolTipSize?: TooltipMaxWidth
  defaultSelected?: T[]
}

export const TypeAheadFieldAdapter = <T extends Record<string, any>>({
  onChange = noop,
  input,
  options,
  label,
  meta: { touched, error, submitError, active } = {},
  disabled,
  readOnly,
  multiSelect = true,
  placeholder,
  labelKey,
  shouldForceTouch = false,
  emptyLabel,
  baseInputStyle,
  inputContainerStyle,
  dropdownContainerStyle,
  filterBy,
  renderMenuItemChildren,
  onInputChange,
  alwaysOpen = false,
  fixedDropdownMenuPosition = false,
  toolTipContent,
  toolTipTriggerConfig,
  toolTipPlacement,
  toolTipHoverEnabled,
  toolTipSize,
  defaultSelected,
}: TypeAheadFieldAdapterProps<T>): JSX.Element => {
  const [showError, setShowError] = useState<boolean>(false)
  const [isFocusedInternal, setIsFocusedInternal] = useState<boolean>(alwaysOpen)
  const [searchText, setSearchText] = useState<string>('')
  const getLabelKeyString = useCallback(
    (option: T): string => {
      if (!labelKey) {
        if (typeof option === 'string') return option
        return option.label || option.name || String(option)
      }
      if (isFunction(labelKey)) {
        return labelKey(option)
      }
      return option[labelKey] || String(option)
    },
    [labelKey],
  )

  const selectedOptions = useMemo(() => {
    return toNativeArray<T>(input.value)
  }, [input.value])

  // Track last selected options from handleChange for blur callback
  const lastSelectedRef = useRef<T[]>([])

  // Convert defaultSelected to initialValues (string array of labels)
  const initialValues = useMemo(() => {
    if (!defaultSelected || defaultSelected.length === 0) return undefined
    return defaultSelected.map((option) => getLabelKeyString(option))
  }, [defaultSelected, getLabelKeyString])

  const handleInputChange = useCallback(
    (text: string) => {
      setSearchText(text)
      onInputChange?.(text)
    },
    [onInputChange],
  )

  // fieldProps for filterBy: text is always '' so TypeAhead handles text filtering internally.
  // This prevents double-filtering where pre-filtering narrows optionsList and then TypeAhead's
  // matchFinder runs on the already-narrowed list (which breaks backspace/delete behavior).
  const filterFieldProps = useMemo(
    (): FilterByFieldProps<T> => ({
      text: '',
      selected: selectedOptions,
      labelKey: getLabelKeyString,
      multiple: multiSelect,
    }),
    [selectedOptions, getLabelKeyString, multiSelect],
  )

  // renderFieldProps for renderMenuItemChildren: text is the actual search text for highlighting.
  const renderFieldProps = useMemo(
    (): FilterByFieldProps<T> => ({
      text: searchText,
      selected: selectedOptions,
      labelKey: getLabelKeyString,
      multiple: multiSelect,
    }),
    [searchText, selectedOptions, getLabelKeyString, multiSelect],
  )

  const filteredOptions = useMemo(() => {
    if (!options) return []
    if (!filterBy) return options
    return options.filter((option) => filterBy(option, filterFieldProps))
  }, [options, filterBy, filterFieldProps])

  const menuItemRenderer = useCallback(
    (option: T, displayText: JSX.Element, _selected?: boolean): JSX.Element => {
      if (renderMenuItemChildren) {
        return renderMenuItemChildren(option, renderFieldProps)
      }
      return displayText
    },
    [renderMenuItemChildren, renderFieldProps],
  )

  // Transform options to add a computed 'label' property when labelKey is a function
  const transformedOptions = useMemo(() => {
    if (!filteredOptions || filteredOptions.length === 0) return []
    const firstOption = filteredOptions[0]
    if (typeof firstOption === 'string') return filteredOptions

    // If labelKey is a function, we need to add a 'label' property to each option
    // so that TypeAheadCustomOptionsConfig can use it
    if (isFunction(labelKey)) {
      return filteredOptions.map((option) => ({
        ...option,
        label: getLabelKeyString(option),
      }))
    }
    return filteredOptions
  }, [filteredOptions, labelKey, getLabelKeyString])

  const customOptionsConfig = useMemo(() => {
    if (!transformedOptions || transformedOptions.length === 0) return undefined
    const firstOption = transformedOptions[0]
    if (typeof firstOption === 'string') return undefined

    const hasCustomRenderer = !!renderMenuItemChildren

    // Determine valueKey: prefer 'value', fallback to 'id', then 'code' (for ICD10), otherwise undefined
    // This is important for FlashList keyExtractor - without a valid valueKey, all items get the same key
    // Use property access to check for actual defined values, not just prototype chain existence
    const valueKey =
      firstOption['value' as keyof T] !== undefined
        ? 'value'
        : firstOption['id' as keyof T] !== undefined
          ? 'id'
          : firstOption['code' as keyof T] !== undefined
            ? 'code'
            : undefined
    return new TypeAheadCustomOptionsConfig<T>(
      transformedOptions,
      hasCustomRenderer ? menuItemRenderer : undefined,
      undefined,
      // Always use 'label' since we transform options to have this property when labelKey is a function
      isFunction(labelKey) ? 'label' : (labelKey as keyof T & string),
      valueKey as (keyof T & string) | undefined,
      ValueFetchMode.VALUE,
    )
  }, [transformedOptions, labelKey, renderMenuItemChildren, menuItemRenderer])

  const stringOptions = useMemo(() => {
    if (!filteredOptions) return []
    return filteredOptions.map((option) => getLabelKeyString(option))
  }, [filteredOptions, getLabelKeyString])

  const handleChange = useCallback(
    (value: TypeAheadValue) => {
      // Try to match by label first, then fall back to matching by id or value
      // Note: val can be string or number at runtime (getValueForItem may return number for 'id')
      const matchOption = (opt: T, val: string | number) => {
        const valStr = String(val)
        // For string options, direct comparison
        if (typeof opt === 'string') return opt === valStr
        // Match by label
        const labelMatch = getLabelKeyString(opt) === valStr
        if (labelMatch) {
          return true
        }
        // Match by id (for customOptionsConfig with valueKey='id')
        if (typeof opt === 'object' && opt !== null && 'id' in opt && String(opt.id) === valStr) {
          return true
        }
        // Match by value property
        if (typeof opt === 'object' && opt !== null && 'value' in opt && String(opt.value) === valStr) {
          return true
        }
        // Match by code property (for ICD10 codes with { code, desc } structure)
        if (typeof opt === 'object' && opt !== null && 'code' in opt && String(opt.code) === valStr) {
          return true
        }
        return false
      }

      // Normalize value to array for consistent processing
      const valueArray = Array.isArray(value) ? value : value !== '' ? [value] : []

      // For multi-select, filter all matching options
      // For single-select, use find to return only the first matching option
      // This prevents duplicates when multiple options share the same value (e.g., customer aliases)
      const newSelectedOptions = multiSelect
        ? options.filter((opt) => valueArray.some((val) => matchOption(opt, val)))
        : (() => {
            const firstVal = valueArray[0]
            if (firstVal === undefined) return []
            const matched = options.find((opt) => matchOption(opt, firstVal))
            return matched ? [matched] : []
          })()

      // Store for blur callback
      lastSelectedRef.current = newSelectedOptions

      // Always pass array to input.onChange for consistency with form submission expectations
      onChange(newSelectedOptions)
      if (input.onChange) input.onChange(newSelectedOptions)
    },
    [onChange, input, options, getLabelKeyString, multiSelect],
  )

  const handleFocus = useCallback(() => {
    setIsFocusedInternal(true)
    if (input.onFocus) input.onFocus()
  }, [input])

  const handleBlur = useCallback(() => {
    if (!alwaysOpen) {
      setIsFocusedInternal(false)
      setSearchText('')
    }
    if (shouldForceTouch && !showError) {
      setShowError(true)
    }
    if (input.onBlur) input.onBlur(lastSelectedRef.current)
  }, [alwaysOpen, shouldForceTouch, showError, input])

  const currentValue = useMemo(() => {
    const nativeValue = toNativeArray<T>(input.value)
    if (nativeValue.length === 0) return multiSelect ? [] : ''
    if (multiSelect) {
      return nativeValue.map((v) => getLabelKeyString(v))
    }
    // For single-select, return a single string value
    return getLabelKeyString(nativeValue[0])
  }, [input.value, getLabelKeyString, multiSelect])

  const displayError = (touched || showError) && (error || submitError) ? error || submitError : undefined

  return (
    <TypeAhead
      label={label}
      value={currentValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onInputChange={handleInputChange}
      isFocused={alwaysOpen || active || isFocusedInternal}
      options={customOptionsConfig ? undefined : stringOptions}
      customOptionsConfig={customOptionsConfig}
      placeholder={placeholder}
      readOnly={disabled || readOnly}
      multiSelect={multiSelect}
      name={input.name}
      inputName={input.name}
      error={displayError}
      showNoResultsDialog={!!emptyLabel}
      baseInputStyle={baseInputStyle}
      inputContainerStyle={inputContainerStyle}
      dropdownContainerStyle={dropdownContainerStyle}
      toolTipContent={toolTipContent}
      toolTipTriggerConfig={toolTipTriggerConfig}
      toolTipPlacement={toolTipPlacement}
      toolTipHoverEnabled={toolTipHoverEnabled}
      toolTipSize={toolTipSize}
      initialValues={initialValues}
      alwaysOpen={alwaysOpen}
      fixedDropdownMenuPosition={fixedDropdownMenuPosition}
    />
  )
}

export default TypeAheadFieldAdapter
